import type { FeatureFinishedPort } from "./FeatureFinishedPort";
import type { FeatureFinishedTuning } from "./FeatureFinishedTuning";
import type { AudioService } from "../../audio/ports/AudioService";
import type { CanvasOverlayStore } from "../../canvas-overlay/CanvasOverlayStore";
import type { BetSystem } from "../../bet/BetSystemTypes";
import type { MoneyFormatter } from "../../money/MoneyFormatter";
import type { LocalizationService } from "../../localization/LocalizationService";
import { multiplyMinorByMultiplier } from "../../money/multiplyMinorByMultiplier";
import { OverlayPressBinding } from "../overlayInput";
import type { Log, PresentationTaskRunner } from "@slot/presentation";

export class FeatureFinishedPresenter {
  private readonly panel: FeatureFinishedPort;
  private readonly log: Log;
  private readonly audioService: AudioService;
  private readonly betSystem: BetSystem;
  private readonly formatter: MoneyFormatter;
  private readonly localization: LocalizationService;
  private readonly tuning: FeatureFinishedTuning;

  private readonly extraSpinsOfferFeatureIds: ReadonlySet<string>;
  private readonly featurePriceMultipliersById: Readonly<Record<string, number>>;
  private readonly extraSpinsOfferCountById: Readonly<Record<string, number>>;
  private readonly onBuyExtraSpins?: (featureId: string) => void;

  /** The chrome runner: the arm delay pauses and frame-steps with the game, never speed-scales. */
  private readonly runner: PresentationTaskRunner;

  private closeRequested = false;
  private buyRequested = false;
  private declineRequested = false;
  /** Presses count only once the buttons are enabled: a key during the enter animation or the arm delay is ignored (finding 11). */
  private armed = false;
  private resolveAction: (() => void) | null = null;

  private readonly pressBinding = new OverlayPressBinding();
  private readonly unbindTaps: Array<() => void> = [];

  private readonly canvasOverlayStore: CanvasOverlayStore;

  public constructor(input: {
    panel: FeatureFinishedPort;
    audioService: AudioService;
    /** The panel is Pixi: the DOM chrome hides while it is up. */
    canvasOverlayStore: CanvasOverlayStore;
    betSystem: BetSystem;
    formatter: MoneyFormatter;
    localization: LocalizationService;
    log: Log;
    /** All client presentation knobs (arm delay + music ids/fades) — the single tuning object. */
    tuning: FeatureFinishedTuning;
    extraSpinsOfferFeatureIds?: readonly string[];
    featurePriceMultipliersById?: Readonly<Record<string, number>>;
    extraSpinsOfferCountById?: Readonly<Record<string, number>>;
    /** The extra-spins buy. Absent in replay mode (no lifecycle): the panel then never makes the offer. */
    onBuyExtraSpins?: (featureId: string) => void;
    runner: PresentationTaskRunner;
  }) {
    this.runner = input.runner;
    this.canvasOverlayStore = input.canvasOverlayStore;
    this.panel = input.panel;
    this.log = input.log;
    this.audioService = input.audioService;
    this.betSystem = input.betSystem;
    this.formatter = input.formatter;
    this.localization = input.localization;
    this.tuning = input.tuning;
    this.extraSpinsOfferFeatureIds = new Set(input.extraSpinsOfferFeatureIds ?? []);
    this.featurePriceMultipliersById = input.featurePriceMultipliersById ?? {};
    this.extraSpinsOfferCountById = input.extraSpinsOfferCountById ?? {};
    this.onBuyExtraSpins = input.onBuyExtraSpins;
  }

  public async presentFeatureFinished(inputArgs: {
    totalWinMinor?: string;
    featureIdentifier?: string;
    onPresentationReady?: () => void;
    /**
     * Fired after the user dismisses the panel on a mode-transition path
     * (simple close OR offer-mode decline). NOT fired on BUY — the player
     * is staying in feature mode there, no transition needed. Invoked
     * before `playExitAnimation` so the caller can kick off the plasm
     * sweep in parallel with the panel's exit, mirroring the trigger-side
     * `onDismissed` pattern.
     */
    onUserDismiss?: () => void;
  }): Promise<void> {

    this.closeRequested = false;

    this.resolveAction = null;
    this.buyRequested = false;
    this.declineRequested = false;
    this.armed = false;

    const formatted = this.formatter.formatMinor(inputArgs.totalWinMinor ?? "0");
    this.panel.setSummary(formatted);

    const offerMode = this.resolveOfferMode(inputArgs.featureIdentifier);

    // Stinger → bed: the intro plays once on the music bus as the panel
    // paints, then the loop crossfades in when the intro source ends.
    // The base-music return happens after the exit animation below; it
    // also serves as the interrupt path if the player dismisses mid-intro.
    this.audioService.control.playMusicWithIntro(
      this.tuning.music.finishedIntroId,
      this.tuning.music.idleId,
    );

    if (offerMode) {
      const currentBetMinor = this.betSystem.getCurrentBetMinor();
      const multiplier = this.featurePriceMultipliersById[inputArgs.featureIdentifier!];
      const costMinor = multiplyMinorByMultiplier(currentBetMinor, multiplier);
      const formattedCost = this.formatter.formatMinor(costMinor);
      const spinCount = this.extraSpinsOfferCountById[inputArgs.featureIdentifier!] ?? 0;

      const costText = this.localization.t(
        "featurefinished.offer.cost",
        "{count} spins cost {cost}",
        { count: spinCount, cost: formattedCost },
      );
      this.panel.setCostText(costText);
      this.panel.setBuyEnabled(false);
      this.panel.setDeclineEnabled(false);
      this.panel.showOffer();

      this.log(`[FeatureFinished] offer mode for feature="${inputArgs.featureIdentifier}" cost=${formattedCost}`);
    } else {
      this.panel.setSkipEnabled(false);
      this.panel.showSimple();

      this.log("[FeatureFinished] simple mode");
    }

    if (inputArgs.onPresentationReady) {
      try {
        inputArgs.onPresentationReady();
      } catch (err) {
        this.log(`[FeatureFinished] onPresentationReady threw: ${err instanceof Error ? err.message : String(err)}`);
      }
    }

    if (offerMode) {
      this.bindOfferInput();
    } else {
      this.bindSimpleInput();
    }

    const releaseOverlay = this.canvasOverlayStore.open();
    try {
      await this.panel.playEnterAnimation();

      await this.runner.sleep(this.tuning.armDelayMs / 1000);

      this.armed = true;
      if (offerMode) {
        this.panel.setBuyEnabled(true);
        this.panel.setDeclineEnabled(true);
      } else {
        this.panel.setSkipEnabled(true);
        // Fade in the "tap to continue" hint at the same moment dismiss
        // becomes active. Fire-and-forget so input isn't blocked while
        // the hint is still fading in. No-op in offer mode (the BUY /
        // DECLINE buttons replace the tap-anywhere path there).
        // Mirrors BigWinView's pattern.
        void this.panel.fadeInContinueText();
      }

      const action = await this.waitForAction();

      if (action !== "buy") {
        inputArgs.onUserDismiss?.();
      }

      await this.panel.playExitAnimation();

      // On BUY, the next spin re-enters the feature immediately — skip the
      // base-music detour and crossfade straight back to the feature loop
      // so there's no audible blip during the buy round-trip. Feature loop
      // uses a 1.5s fade-in to match the transition pacing on the trigger
      // path; base music keeps the default fade.
      if (action === "buy") {
        // Per-feature free-spins loop when configured (e.g. vs_rush_buy → its own track),
        // else the shared default loop.
        const loopId =
          (inputArgs.featureIdentifier && this.tuning.music.loopIdByFeatureId?.[inputArgs.featureIdentifier]) ||
          this.tuning.music.loopId;
        this.audioService.control.setMusicTrack(loopId, this.tuning.music.loopFadeMs);
      } else {
        this.audioService.control.setMusicTrack(this.tuning.music.baseId, this.tuning.music.baseFadeMs);
      }

      this.panel.hide();

      if (action === "buy" && inputArgs.featureIdentifier) {
        this.log(`[FeatureFinished] buy extra spins for feature="${inputArgs.featureIdentifier}"`);
        this.onBuyExtraSpins!(inputArgs.featureIdentifier);
      } else {
        this.log("[FeatureFinished] closed");
      }
    } finally {
      releaseOverlay();
      this.armed = false;
      this.unbindAllInput();
      this.panel.setSkipEnabled(false);
      this.panel.setBuyEnabled(false);
      this.panel.setDeclineEnabled(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Mode resolution
  // ---------------------------------------------------------------------------

  private resolveOfferMode(featureIdentifier?: string): boolean {
    if (!featureIdentifier) return false;
    if (!this.extraSpinsOfferFeatureIds.has(featureIdentifier)) return false;

    const multiplier = this.featurePriceMultipliersById[featureIdentifier];
    if (typeof multiplier !== "number" || !Number.isFinite(multiplier) || multiplier <= 0) return false;

    if (!this.onBuyExtraSpins) return false;
    if (!this.panel.hasOffer) return false;

    return true;
  }

  // ---------------------------------------------------------------------------
  // Simple mode input (existing pattern)
  // ---------------------------------------------------------------------------

  private bindSimpleInput(): void {
    const onPress = (): void => {
      if (!this.armed) return;
      this.closeRequested = true;
      this.resolveAction?.();
      this.log("[FeatureFinished] close requested");
    };

    this.unbindTaps.push(this.panel.bindSkipTap(onPress));
    this.pressBinding.bindKeys(onPress);
  }

  // ---------------------------------------------------------------------------
  // Offer mode input
  // ---------------------------------------------------------------------------

  private bindOfferInput(): void {
    const onBuy = (): void => {
      if (!this.armed) return;
      this.buyRequested = true;
      this.resolveAction?.();
      this.log("[FeatureFinished] buy requested");
    };

    const onDecline = (): void => {
      if (!this.armed) return;
      this.declineRequested = true;
      this.resolveAction?.();
      this.log("[FeatureFinished] decline requested");
    };

    this.unbindTaps.push(this.panel.bindBuyTap(onBuy), this.panel.bindDeclineTap(onDecline));
    // Keyboard maps to decline (safe default)
    this.pressBinding.bindKeys(onDecline);
  }

  // ---------------------------------------------------------------------------
  // Shared
  // ---------------------------------------------------------------------------

  private unbindAllInput(): void {
    for (const unbind of this.unbindTaps) unbind();
    this.unbindTaps.length = 0;
    this.pressBinding.unbindAll();
  }

  private async waitForAction(): Promise<"close" | "buy" | "decline"> {
    if (!this.closeRequested && !this.buyRequested && !this.declineRequested) {
      await new Promise<void>((resolve) => {
        this.resolveAction = resolve;
      });
    }
    if (this.buyRequested) return "buy";
    return this.declineRequested ? "decline" : "close";
  }
}
